import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { ThemePalette } from '@angular/material/core';
import { RegisteredRadioButton } from './ts-radio-group.interface';

@Component({
  selector: 'ts-radio-group',
  standalone: true,
  imports: [MatRadioModule],
  styleUrls: ['./ts-radio-group.component.scss'],
  template: `
    <mat-radio-group
      [color]="color"
      [disabled]="isDisabled"
      [name]="name"
      [labelPosition]="labelPosition"
      [required]="required"
    >
      <ng-content />
    </mat-radio-group>
  `,
  host: { class: 'ts-radio-group' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TsRadioGroupComponent,
      multi: true,
    },
  ],
})
export class TsRadioGroupComponent implements ControlValueAccessor {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly buttons = new Set<RegisteredRadioButton>();

  @Input() color: ThemePalette = 'primary';
  @Input() disabled = false;
  @Input() name: string;
  @Input() labelPosition: 'before' | 'after' = 'after';
  @Input() required = false;

  @Output() change = new EventEmitter<MatRadioChange>();

  value: unknown;
  private formDisabled = false;

  get isDisabled(): boolean {
    return this.disabled || this.formDisabled;
  }

  writeValue(value: unknown): void {
    this.value = value;
    this.cdr.markForCheck();
    this.refreshButtons();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  registerButton(button: RegisteredRadioButton): void {
    this.buttons.add(button);
  }

  unregisterButton(button: RegisteredRadioButton): void {
    this.buttons.delete(button);
  }

  onValueChange(value: unknown, source: RegisteredRadioButton | null = null): void {
    if (this.value === value) {
      return;
    }
    this.value = value;
    this.onChange(value);
    this.onTouched();
    this.refreshButtons();
    this.change.emit({ source, value } as unknown as MatRadioChange);
  }

  private refreshButtons(): void {
    this.buttons.forEach((button) => button.markForCheck());
  }

  private onChange: (value: unknown) => void = () => {};
  private onTouched: () => void = () => {};
}
